import React, { useEffect } from 'react';
import { useProcessingContext } from './processing';
import { useStatusContext } from './status';
import { useEngagementContext } from './engagement';
import { ProviderParams } from '../models/react';
import { SDKMessage, SDKMessageType, SDKParticipantType, SDKUserContext } from '../models/sdk';
import { MSGType, MSGParsed, MSGItem, MSGItemType, MSGItemBody, MSGNewContextType, MSGContextType, MSGPartyType, MSGWait } from '../models/msg';
import { useLanguageContext } from './language';
import { useUserContext } from './user';
import { UserContextType, UserDefaultType } from '../models/user';
import useSessionStorage from '../hooks/sessionStorage.hook';

const MsgContext = React.createContext<MSGContextType | undefined>(undefined);
export function useMsgContext() {
    return React.useContext(MsgContext);
}

const NewMsgContext = React.createContext<MSGNewContextType | undefined>(undefined);
export function useNewMsgContext(): MSGNewContextType | undefined {
    return React.useContext(NewMsgContext);
}


export default function MsgProvider({ children }: ProviderParams) {
    const processingCtxt = useProcessingContext();
    if (!processingCtxt) throw new Error('Missing processing context');

    const statusCtxt = useStatusContext();
    if (!statusCtxt) throw new Error('Missing header context');

    const engagementCtxt = useEngagementContext();
    if (!engagementCtxt) throw new Error('Missing engagement context');

    const languageCtxt = useLanguageContext();
    if (!languageCtxt) throw new Error('Missing language context');

    const userContext = useUserContext()
    if (!userContext) throw new Error('Missing user context');

    const setIsBusy = processingCtxt.setIsBusy;

    // const engagement_hook = useSessionStorage('engagement');
    const dialog_hook = useSessionStorage("dialog");


    const [newMessage, setNewMessage] = React.useState('');
    const [messages, setMessages] = React.useState<Array<MSGItem>>([]);
    const [lastTemp, setLastTemp] = React.useState<MSGItem | undefined>(undefined)

    // MESSAGES
    useEffect(() => {
        const messages_recovered = recoverMessageAtSession()
        const temp = messages_recovered.find(message => message.isTemp)
        setMessages(messages_recovered)
        setLastTemp(temp)
    }, [])

    useEffect(() => {
        saveMessageAtSession()
    }, [messages])

    function recoverLastTemp() {
        if (lastTemp && typeof lastTemp.body === 'string') {
            addMessage(lastTemp.body, SDKParticipantType.SYSTEM, true)
            setLastTemp(undefined)
        }
    }



    function saveMessageAtSession() {
        dialog_hook.setValue(JSON.stringify(messages) || "[]");
    }
    function recoverMessageAtSession(): Array<MSGItem> {
        if (dialog_hook.value) {
            const messages_recovered = JSON.parse(dialog_hook.value) as Array<MSGItem>
            return messages_recovered
        }
        return []
    }
    async function sendNewMessage(): Promise<void> {
        if (!newMessage) return;
        return sendMessage(newMessage);
    };

    async function sendMessage(message: string | MSGItemBody): Promise<void> {
        if (!engagementCtxt?.engagement) {
            console.warn(`User attempts to send a message but no engagement is found:\n  User's message: '${message}'`);
            addMessage('Current session was terminated.', MSGPartyType.UI);
            return;
        };

        addMessages([formatMessage('sending...', MSGPartyType.UI, true)]);
        setNewMessage('');

        setIsBusy(true);
        let response = null
        if (languageCtxt?.status) {
            const newMessage = await languageCtxt.translateMessage(message, SDKParticipantType.CUSTOMER)
            if (newMessage) {
                console.debug("Translation: Enabled - translated message sent to agent")
                response = await engagementCtxt.engagement.sendMessage(newMessage);
                addMessage(message as string, SDKParticipantType.CUSTOMER, false, '', false, newMessage);
                if (response)
                    languageCtxt.saveTranscript(message as string, response.messageId, SDKParticipantType.CUSTOMER, userContext?.getUserInfo() as UserDefaultType, engagementCtxt.engagement.engagementId)
            } else {
                console.debug("Translation: Error - Sending original message")
                response = await engagementCtxt.engagement.sendMessage(message);
            }
        } else {
            console.debug("Translation: Disabled - Sending original message")
            response = await engagementCtxt.engagement.sendMessage(message);

        }
        setIsBusy(false);
        if (!response) throw new Error('Failed to send message');

        console.debug(response.messageId);
        if (engagementCtxt?.engagement?.participants.some(p=> p.participantType == SDKParticipantType.SYSTEM)){
            addEllipsisMessages(messages[messages.length-1])
        }

        return;
    };

    function addMessages(message: Array<MSGItem>, keepTemp: boolean = false): void {
        setMessages(prevState => {
            const prev = prevState.map(el => el)
            if (keepTemp) {
                return [...prev, ...message]
            }
            const last = prev.pop(),
                isTemp = last?.isTemp,
                newState = isTemp ? prev : prevState;

            return [...newState, ...message];
        });
    }

    function addEllipsisMessages(lastMessage: MSGItem) {
        setTimeout(()=>{

            const ellipsisMessage: MSGWait = {
                "id": "eai9rpg4srj",
                "direction": MSGItemType.incoming,
                "party": SDKParticipantType.SYSTEM,
                "displayName": "",
                "isTemp": true,
                "type": MSGType.ellipsis,
                "original": "",
                "body":''
                // body: {elementType: SDKMessageType.ellipsis}
            };
            if (lastMessage.body == messages[messages.length-1]?.body) {
                addMessages([ellipsisMessage]);
            }
        }, 500);
    
    }


    function removeTempMessage() {
        setMessages(prevState => {
            const prev = prevState.map(el => el),
                last = prev.pop(),
                isTemp = last?.isTemp,
                newState = isTemp ? prev : prevState;

            return [...newState];
        });
    }

    function addMessage(message: string, participant: SDKParticipantType | MSGPartyType, isTemp = false, name = '', keepTemp = false, original = ''): void {
        const msg = formatMessage(message, participant, isTemp, name, MSGType.text, original);
        addMessages([msg], keepTemp);
    }

    function addRichMessage(message: SDKMessage): void {
        const sender = message.senderParticipant,
            content = message.body.elementText.text.trim().split(/<end>\n|<end>/).filter(msg => msg),
            messages: Array<MSGItem> = [];

        content.forEach((str: string) => {
            const parsed: MSGParsed = JSON.parse(str),
                type = getType(parsed.body.elementType),
                body = getBody(parsed),
                isTemp = type === "ellipsis" ? true : false,
                message = formatMessage(body, sender.participantType, isTemp, sender.displayName, type);

            console.log(sender.participantType + ' says:', body, '\n message:', message, '\n parsed:', parsed);
            messages.push(message);
        });

        addMessages(messages);
    }

    function clearDialog(isAll = false): void {
        if (isAll) setMessages(prevState => {
            const last = prevState.filter(msg => MSGPartyType.UI === msg.party).pop();
            return last ? [last] : [];
        });
        else {
            setMessages([]);
        }
    }

    // PARTICIPANTS

    function updateParticipant(message: SDKUserContext, isAdd: boolean): void {
        const name = message.participant.displayName,
            text = isAdd ? `${name} has joined.` : `${name} has left.`,
            msg = formatMessage(text, MSGPartyType.UI);

        addMessages([msg]);
    }

    return (
        <MsgContext.Provider value={messages}>
            <NewMsgContext.Provider value={{ newMessage, setNewMessage, sendNewMessage, sendMessage, addMessage, removeTempMessage, recoverLastTemp, addRichMessage, clearDialog, updateParticipant }}>
                {children}
            </NewMsgContext.Provider>
        </MsgContext.Provider>
    );
}

function formatMessage(body: string | MSGItemBody, participant: SDKParticipantType | MSGPartyType, isTemp = false, name = '', type = MSGType.text, original = ''): MSGItem {
    return {
        id: Math.random().toString(36).substring(2, 15),
        direction: 'CUSTOMER' === participant ? MSGItemType.outgoing : MSGItemType.incoming,
        party: participant,
        displayName: name || '',
        isTemp: isTemp,
        type: type,
        body: body,
        original: original
    } as MSGItem;
}

function getType(type: SDKMessageType): MSGType {
    switch (type) {
        case SDKMessageType.plaintext: return MSGType.text;
        case SDKMessageType.buttons: return MSGType.options;
        case SDKMessageType.ellipsis: return MSGType.ellipsis;
        default: return MSGType.unknown;
    }
}

function getBody(content: MSGParsed): string | MSGItemBody {
    switch (content.body.elementType) {
        case 'plaintext': return content.body.elementText.text;
        case 'buttons': return {
            text: content.body.elementText.text,
            options: content.body.richMediaPayload?.actions || []
        };
        case 'ellipsis': return "";
        default: return 'unknown';
    }
}
